import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_REDIRECT = '/hrms?tab=training&section=assignments';

function sanitizeRedirect(raw) {
  if (!raw || typeof raw !== 'string') return DEFAULT_REDIRECT;
  const path = raw.trim();
  if (!path.startsWith('/hrms')) return DEFAULT_REDIRECT;
  if (path.startsWith('//') || path.includes('://')) return DEFAULT_REDIRECT;
  return path;
}

/**
 * External SSO consume page — QMS redirects here with a one-time token.
 */
function SsoConsume() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const token = searchParams.get('token');
      const redirectHint = sanitizeRedirect(searchParams.get('redirect'));
      if (!token) {
        setError('Missing SSO token');
        return;
      }

      try {
        const res = await axios.get('/api/auth/sso-consume', {
          params: { token }
        });
        if (cancelled) return;
        const { token: sessionToken, user, redirectPath } = res.data || {};
        if (!sessionToken || !user) {
          setError('SSO response incomplete');
          return;
        }
        applySession(sessionToken, user);
        navigate(sanitizeRedirect(redirectPath || redirectHint), { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || 'SSO sign-in failed');
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [searchParams, applySession, navigate]);

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2} p={3}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
          Go to login
        </Button>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
      <CircularProgress />
      <Typography color="text.secondary">Signing you into Fleetwright…</Typography>
    </Box>
  );
}

export default SsoConsume;
